"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { SurveyQuestion, SurveyQuestionResponse } from "@yardzen/api-dtos";
import { Alert } from "@yardzen/ui/components/alert";
import { H2 } from "@yardzen/ui/components/h2";
import { IconName } from "@yardzen/ui/components/icon";
import parse from "html-react-parser";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  handleAddressQuestionSubmit,
  handleDynamicMultipleTextFieldsQuestionSubmit,
  handleMultipleChoiceQuestionSubmit,
  handleMultipleTextFieldsQuestionSubmit,
  handleSingleChoiceQuestionSubmit,
  handleTextQuestionSubmit,
  setSurveyResponseCookie,
} from "../actions";
import { BackButton } from "./BackButton";
import { PageActions } from "./PageActions";
import { QuizResponse } from "./responses";
import { SkipButton } from "./SkipButton";
import { SubmitButton } from "./SubmitButton";
export type SingleChoiceResponseType = {
  value: string;
  text: string;
};

export type MultipleChoiceResponseType = {
  answers: SingleChoiceResponseType[];
  fields?: {
    key: string;
    value: string;
  }[];
};

export type TextResponseType = {
  answer: string;
};

export type MultipleTextFieldsResponsesType = {
  key: string;
  value: string;
}[];

export type DynamicMultipleTextFieldsResponseType = {
  key: string;
  value: string;
}[];

export interface QuizQuestionHandlerProps {
  question: SurveyQuestion;
  surveyId: string;
  surveyKey: string;
  surveyResponseId: string;
  surveyQuestionResponse?: SurveyQuestionResponse;
  previousQuestionKey?: string;
  basePath: string;
  completionRedirect: string;
  designProjectId?: string;
}

type QuestionResponseType =
  | "text"
  | "single-choice"
  | "address"
  | "multiple-choice"
  | "multiple-text-fields"
  | "dynamic-multiple-text-fields";

export type QuestionOption = {
  value: string;
  text: string;
  icon: IconName;
};

type QuestionFormat = {
  type: QuestionResponseType;
  options?: QuestionOption[];
  maxLength?: number;
  placeholder?: string;
  fields?: { key: string; label: string }[];
};

const questionSubmitMap: Record<
  QuestionResponseType,
  (...args: any[]) => Promise<void>
> = {
  text: handleTextQuestionSubmit,
  "single-choice": handleSingleChoiceQuestionSubmit,
  address: handleAddressQuestionSubmit,
  "multiple-choice": handleMultipleChoiceQuestionSubmit,
  "multiple-text-fields": handleMultipleTextFieldsQuestionSubmit,
  "dynamic-multiple-text-fields": handleDynamicMultipleTextFieldsQuestionSubmit,
};

export function QuizQuestionHandler({
  question,
  surveyKey,
  surveyId,
  surveyResponseId,
  surveyQuestionResponse,
  previousQuestionKey,
  basePath,
  completionRedirect,
  designProjectId,
}: QuizQuestionHandlerProps) {
  const pathname = usePathname();
  const previousQuestionUrl = `${basePath}/${surveyKey}/${previousQuestionKey}`;
  const {
    format,
    text: questionText,
    description: questionAlert,
    required,
    index,
    section,
  } = question;
  const isFirstQuestion = index === 0 && section === 0;
  const questionFormat: QuestionFormat = JSON.parse(format);
  const {
    type: responseType,
    options,
    maxLength,
    placeholder,
    fields,
  } = questionFormat;

  const multipleChoiceSchema = z
    .object({
      values: z.array(z.string()),
      ...fields?.reduce(
        (acc, field) => ({
          ...acc,
          [field.key]: z.string(),
        }),
        {},
      ),
    })
    .refine(
      (data: Record<string, string | string[]>) => {
        if (!required) return true;
        const hasValues = data.values.length > 0;
        const hasFieldValue = fields?.some(
          (field) => data[field.key]?.length > 0,
        );
        return hasValues || hasFieldValue;
      },
      {
        message: "Please select at least one option or fill in a field",
      },
    );
  const singleChoiceSchema = z.object({
    response: required
      ? z.string().min(1, "Please select an option")
      : z.string(),
  });
  const textSchema = z.object({
    response: required ? z.string().min(1, "Please enter a value") : z.string(),
  });
  const multipleTextFieldsSchema = z.object({
    ...fields?.reduce(
      (acc, field) => ({
        ...acc,
        [field.key]: required
          ? z.string().min(1, "Please enter a value")
          : z.string(),
      }),
      {},
    ),
  });

  const dynamicMultipleTextFieldsSchema = z
    .object({
      dynamicFields: z.record(z.string(), z.string()),
    })
    .refine(
      (data) =>
        Object.values(data.dynamicFields).some(
          (val) => !!val && val.trim().length > 0,
        ),
      { message: "Please fill in at least one field" },
    );

  const defaultValues: Record<string, any> = {
    dynamicFields: {},
    response: "",
    values: [],
    ...(fields?.reduce(
      (acc, field) => ({
        ...acc,
        [field.key]: "",
      }),
      {},
    ) as Record<string, string>),
  };

  type FormValuesSchema =
    | z.infer<typeof multipleTextFieldsSchema>
    | z.infer<typeof singleChoiceSchema>
    | z.infer<typeof textSchema>
    | z.infer<typeof multipleChoiceSchema>
    | z.infer<typeof dynamicMultipleTextFieldsSchema>;
  let schema:
    | typeof multipleTextFieldsSchema
    | typeof singleChoiceSchema
    | typeof textSchema
    | typeof multipleChoiceSchema
    | typeof dynamicMultipleTextFieldsSchema;

  let multipleChoiceResponse: MultipleChoiceResponseType | null;
  let singleChoiceResponse: SingleChoiceResponseType | null;
  let textResponse: TextResponseType | null;
  let multiTextResponses: MultipleTextFieldsResponsesType;
  let dynamicTextResponses: MultipleTextFieldsResponsesType;

  switch (responseType) {
    case "multiple-choice":
      schema = multipleChoiceSchema;
      multipleChoiceResponse = surveyQuestionResponse?.response
        ? (JSON.parse(
            surveyQuestionResponse.response,
          ) as MultipleChoiceResponseType)
        : null;
      if (multipleChoiceResponse) {
        defaultValues.values = multipleChoiceResponse.answers.map((answer) =>
          answer.value.toString(),
        );
        multipleChoiceResponse.fields?.forEach((field) => {
          defaultValues[field.key] = field.value;
        });
      }
      break;

    case "single-choice":
      schema = singleChoiceSchema;
      singleChoiceResponse = surveyQuestionResponse?.response
        ? (JSON.parse(
            surveyQuestionResponse.response,
          ) as SingleChoiceResponseType)
        : null;
      if (singleChoiceResponse) {
        defaultValues.response = singleChoiceResponse.value;
      }
      break;

    case "address":
    case "text":
      schema = textSchema;
      textResponse = surveyQuestionResponse?.response
        ? (JSON.parse(surveyQuestionResponse.response) as TextResponseType)
        : null;
      if (textResponse) {
        defaultValues.response = textResponse.answer;
      }
      break;

    case "multiple-text-fields":
      schema = multipleTextFieldsSchema;
      multiTextResponses = surveyQuestionResponse?.response
        ? JSON.parse(surveyQuestionResponse.response)
        : [];
      multiTextResponses.forEach((response) => {
        defaultValues[response.key] = response.value;
      });
      break;

    case "dynamic-multiple-text-fields":
      schema = dynamicMultipleTextFieldsSchema;
      dynamicTextResponses = surveyQuestionResponse?.response
        ? JSON.parse(surveyQuestionResponse.response)
        : [];
      if (Array.isArray(dynamicTextResponses)) {
        dynamicTextResponses.forEach((response) => {
          if (!defaultValues.dynamicFields) defaultValues.dynamicFields = {};
          defaultValues.dynamicFields[response.key] = response.value;
        });
      }
      break;
  }

  const {
    register,
    formState: { isValid },
    setValue,
    getValues,
  } = useForm<FormValuesSchema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (surveyResponseId && surveyId) {
      setSurveyResponseCookie(surveyId, surveyResponseId);
    }
  }, [surveyId, surveyResponseId]);

  // Safari does not scroll to the top of the page, so let's do it manually.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const formAction = async (formData: FormData) => {
    return questionSubmitMap[responseType](
      surveyId,
      surveyResponseId,
      surveyKey,
      question.id,
      question.questionKey,
      options,
      fields?.map((field) => field.key),
      formData,
      basePath,
      completionRedirect,
      designProjectId,
    );
  };

  const quizRendersSkipped = pathname === "/design-consultation/design-quiz";
  const quizAsFirstStepCopy =
    "Every yard is unique. Answer a few quick questions, and we'll recommend a landscape design plan based on your need.";

  const shouldReverseAlertOrder =
    pathname.includes("address") || quizRendersSkipped;

  const alertIcon = pathname.includes("address") ? "location-pin" : "";

  const alertContent = (questionAlert || quizRendersSkipped) && (
    <Alert
      layout="row"
      customIcon={quizRendersSkipped ? "stars" : (alertIcon as IconName)}
    >
      {quizRendersSkipped ? quizAsFirstStepCopy : parse(questionAlert || "")}
    </Alert>
  );

  return (
    <form
      id="design-consultation-quiz-form"
      action={formAction}
      className="flex w-full flex-col justify-start gap-4 p-4 sm:items-center sm:justify-center sm:pt-20"
    >
      <div className="flex w-full flex-col items-center gap-4 md:justify-center md:gap-8">
        {!shouldReverseAlertOrder && alertContent}
        <H2 className="text-left md:text-center">
          {parse(questionText || "")}
        </H2>
        {shouldReverseAlertOrder && alertContent}
        <QuizResponse
          responseType={responseType}
          options={options}
          maxLength={maxLength}
          placeholder={placeholder}
          fields={fields}
          surveyQuestionResponse={surveyQuestionResponse}
          register={register}
          setValue={setValue}
          getValues={getValues}
        />
        {!required && (
          <div className="flex w-full flex-1 flex-row items-end justify-center py-1 sm:flex-initial sm:items-center">
            <SkipButton />
          </div>
        )}
        <PageActions className={required ? "" : "sm:py-2"}>
          <BackButton
            backUrl={previousQuestionUrl}
            isFirstQuestion={isFirstQuestion}
          />
          <SubmitButton
            disabled={!isValid}
            questionKey={question.questionKey}
          />
        </PageActions>
      </div>
    </form>
  );
}
